import { ChangeDetectorRef, Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChatbotUserService } from '../../services/chatbot-user.service';
import { UploadProgressService } from '../../services/upload-progress.service';
import { UploadProgressPanelComponent } from '../../components/upload-progress-panel/upload-progress-panel.component';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AdminLayoutMode } from '../../config/layout.config';


@Component({
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    UploadProgressPanelComponent
  ],
  templateUrl: './default-layout.component.html'
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  readonly layoutMode: AdminLayoutMode = environment.adminLayoutMode ?? 'sidebar';
  readonly useHorizontalNav = this.layoutMode === 'horizontal';

  private readonly auth = inject(AuthService);
  protected readonly uploadProgress = inject(UploadProgressService);
  protected readonly isLoggedIn = toSignal(this.auth.isLoggedIn$, { initialValue: false });
  protected readonly uploadItems = toSignal(this.uploadProgress.items$, { initialValue: [] });
  protected readonly hasIncompleteUploads = computed(() =>
    this.uploadItems().some(i => !i.completed)
  );
  protected readonly hasUploadInProgress = computed(() =>
    this.uploadItems().some(
      i => !i.completed && i.file instanceof File
    )
  );
  protected readonly userService = inject(ChatbotUserService);

  defaultPageTitle = 'Clientes / Proyectos';
  pageTitle: string = this.defaultPageTitle;
  appName = 'SIM CHATBOT';
  isSidebarCollapsed = false;
  menuState: Record<string, boolean> = {};
  private routerSubscription?: Subscription;
  private readonly collapseBreakpoint = 767.98;
  private readonly menuRoutePrefixes = {
    'admin': ['/admin']
  };

  private readonly pageTitles = [
    { prefix: '/admin/client', title: 'Clientes / Proyectos' },
    { prefix: '/admin/api-client', title: 'Clientes / API' }
  ];

  readonly menuItems: any[] = [
    { label: 'Clientes / Proyectos', route: '/', iconClass: 'bi bi-file-earmark-text' },
    { label: 'Clientes / API', route: '/admin/api-client', iconClass: 'bi bi-braces-asterisk' }
  ];

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.userService.updateLastLoginDate().subscribe();
  }

  ngOnInit(): void {
    if (!this.useHorizontalNav) {
      this.isSidebarCollapsed = window.innerWidth <= this.collapseBreakpoint;
    }
    this.syncMenusWithRoute(this.router.url);
    this.updatePageTitle(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.syncMenusWithRoute(event.urlAfterRedirects);
        this.updatePageTitle(event.urlAfterRedirects);
      });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  protected get displayName(): string | null {
    return this.auth.displayName;
  }

  protected login(): void {
    this.auth.login();
  }

  protected logout(): void {
    this.auth.logout();
  }

  toggleSidebar(): void {
    if (this.useHorizontalNav) {
      return;
    }
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  toggleMenu(key: string): void {
    const submenu: HTMLElement = document.querySelector(`[data-ap-parent="${key}"]`);
    if (submenu) {
      let maxHeight = 0;
      const links = submenu.querySelectorAll('.nav-link');
      links.forEach((link: HTMLElement) => {
        maxHeight += link.offsetHeight;
      });
      submenu.style.maxHeight = `${maxHeight}px`;
    }
    this.menuState[key] = !this.isMenuOpen(key);

    if (!this.menuState[key]) {
      submenu.style.maxHeight = '0';
    }
  }

  isMenuOpen(key: string): boolean {
    return Boolean(this.menuState[key]);
  }

  private syncMenusWithRoute(url: string): void {
    let hasOpenMenu = false;
    for (const key of Object.keys(this.menuRoutePrefixes)) {
      const isOpen = this.menuRoutePrefixes[key].some(prefix => url.startsWith(prefix));
      this.menuState[key] = isOpen;
      // console.log(key, isOpen);
      if (isOpen) {
        hasOpenMenu = true;
        break;
      }
    }
    if (!hasOpenMenu) {
      this.menuState['home'] = true;
    }
  }

  private updatePageTitle(url: string): void {
    const match = this.pageTitles.find(item => url.startsWith(item.prefix));
    this.pageTitle = match ? match.title : this.defaultPageTitle;
    // console.log('url: ' + url, 'pageTitle: ' + this.pageTitle);
    document.title = `${this.appName} - ${this.pageTitle}`;

    this.cdr.detectChanges();
  }
}

